import React, { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import moment from 'moment';
import AppModal from '../../common/AppModal';
import RichTextEditor from '../../common/RichTextEditor';
import { addBusinessDays, getFullAddress, normalizeRichTextValue } from '../../../helpers/global';

const toId = user => String(user?._id || user || '');

const RevisionRequestModal = ({
  show,
  order,
  service,
  currentUser,
  submitting,
  onHide,
  onSubmit
}) => {
  const [description, setDescription] = useState('');
  const [dueInBusinessDays, setDueInBusinessDays] = useState('1');
  const [showValidation, setShowValidation] = useState(false);
  const [selectedRecipientIds, setSelectedRecipientIds] = useState([]);

  useEffect(() => {
    if (!show) return;
    setDescription('');
    setDueInBusinessDays('1');
    setSelectedRecipientIds([]);
    setShowValidation(false);
  }, [show, order?._id]);

  const recipients = useMemo(() => {
    const currentUserId = toId(currentUser);
    const byId = new Map();
    const addRecipients = (users, roleLabel) => {
      (users || []).forEach(user => {
        const id = toId(user);
        if (!id || id === currentUserId) return;
        const existing = byId.get(id);
        if (existing) {
          if (!existing.roles.includes(roleLabel)) existing.roles.push(roleLabel);
        } else {
          byId.set(id, { id, name: user?.name || 'Unknown user', roles: [roleLabel] });
        }
      });
    };

    addRecipients(order?.assignedProcessors, 'Assigned Processor');
    addRecipients(order?.assignedReportAnalysts, 'Assigned Report Analyst');
    return [...byId.values()];
  }, [currentUser, order]);

  const currentUserOrderRole = useMemo(() => {
    const currentUserId = toId(currentUser);
    if (!currentUserId) return null;
    const roles = [];
    if ((order?.assignedProcessors || []).some(user => toId(user) === currentUserId)) {
      roles.push('Assigned Processor');
    }
    if ((order?.assignedReportAnalysts || []).some(user => toId(user) === currentUserId)) {
      roles.push('Assigned Report Analyst');
    }
    return roles.length ? roles.join(' / ') : null;
  }, [currentUser, order]);

  const toggleRecipient = id => {
    setSelectedRecipientIds(previous =>
      previous.includes(id) ? previous.filter(value => value !== id) : [...previous, id]
    );
  };

  const dueDate = useMemo(() => {
    const days = Number(dueInBusinessDays);
    if (!Number.isInteger(days) || days < 1) return '';
    return addBusinessDays(moment().startOf('day'), days)?.format('YYYY-MM-DD') || '';
  }, [dueInBusinessDays]);

  const isValid = Boolean(normalizeRichTextValue(description)) && Boolean(dueDate);
  const orderService = service || order?.service;
  const propertyAddress = order?.fullAddress || getFullAddress(orderService);

  const handleSubmit = () => {
    if (!isValid) {
      setShowValidation(true);
      return;
    }
    onSubmit({
      description: normalizeRichTextValue(description),
      dueDate,
      assignedProcessors: selectedRecipientIds
    });
  };

  return (
    <AppModal
      show={show}
      onHide={onHide}
      onSubmit={handleSubmit}
      heading="Mark for revision"
      submitting={submitting}
      submitLabel="Mark for revision"
      dialogClassName="revision-request-modal"
      bodyClassName="px-4 py-3"
    >
      <div className="text-muted tinyFont mb-3">
        {orderService?.accurateId && (
          <>
            Order <span className="font-weight-semibold">{orderService.accurateId}</span>
          </>
        )}
        {propertyAddress && <span>{orderService?.accurateId ? ' · ' : ''}{propertyAddress}</span>}
        {order?.accurateId && (
          <span>{orderService?.accurateId || propertyAddress ? ' · ' : ''}Lien Search {order.accurateId}</span>
        )}
      </div>
      <Form.Group className="mb-4">
        <Form.Label className="font-weight-semibold mb-2">
          What needs to be revised <span className="text-danger">*</span>
        </Form.Label>
        <RichTextEditor
          className="estoppel-notes-rich-text"
          value={description}
          onChange={setDescription}
          placeholder="Be specific. This text becomes the task."
          enableLists
          height="150px"
          disabled={submitting}
          error={showValidation && !normalizeRichTextValue(description) ? 'Describe what needs to be revised.' : ''}
        />
      </Form.Group>

      <Form.Group className="mb-4">
        <Form.Label className="font-weight-semibold mb-0">Notify</Form.Label>
        <p className="text-muted tinyFont mb-2">Select the people who should receive the revision task.</p>
        {recipients.length ? (
          <div className="scrollbar-thin border rounded bg-white overflow-auto" style={{ maxHeight: '184px' }}>
            {recipients.map((recipient, index) => (
              <div
                key={recipient.id}
                className={`d-flex align-items-start m-0 px-3 py-2${index < recipients.length - 1 ? ' border-bottom' : ''}`}
              >
                <Form.Check.Input
                  id={`revision-recipient-${recipient.id}`}
                  type="checkbox"
                  className="position-static mt-1 ml-0 mr-2 flex-shrink-0"
                  checked={selectedRecipientIds.includes(recipient.id)}
                  disabled={submitting}
                  onChange={() => toggleRecipient(recipient.id)}
                />
                <Form.Label htmlFor={`revision-recipient-${recipient.id}`} className="flex-grow-1 mb-0">
                  <span className="font-weight-semibold">{recipient.name}</span>
                  <span className="text-muted smallFont d-block">{recipient.roles.join(' / ')}</span>
                </Form.Label>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted smallFont mb-0">No other assigned users are available to notify.</p>
        )}
      </Form.Group>

      <div className="d-flex flex-column flex-sm-row">
        <div className="w-100 mb-3 mb-sm-0 mr-sm-3">
          <Form.Group className="mb-0">
            <Form.Label className="font-weight-semibold mb-2">
              Due in (business days) <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              min="1"
              step="1"
              value={dueInBusinessDays}
              disabled={submitting}
              onChange={event => setDueInBusinessDays(event.target.value.replace(/\D/g, ''))}
              isInvalid={showValidation && !dueDate}
            />
            <Form.Control.Feedback type="invalid">Enter at least one business day.</Form.Control.Feedback>
          </Form.Group>
        </div>
        <div className="w-100">
          <Form.Group className="mb-0">
            <Form.Label className="font-weight-semibold mb-2">Due date</Form.Label>
            <Form.Control type="date" value={dueDate} readOnly />
          </Form.Group>
        </div>
      </div>
    </AppModal>
  );
};

export default RevisionRequestModal;
